/** CSS contract: css-library/components/01-leaves/card.css. */
import type { ComponentProps, Ref } from 'react';
import type { HTMLAttributes, KeyboardEvent, ReactNode } from 'react';
import { cx } from '../lib/cx';

interface CardBaseProps extends HTMLAttributes<HTMLElement> {
  isActive?: boolean;
  isColumnHeader?: boolean;

  isDisabled?: boolean;
  isDragOver?: boolean;
  droppable?: boolean;
}

type CardLoadingProps =
  | { isLoading?: false; loadingLabel?: undefined }
  | {
      isLoading: boolean;
      loadingLabel: ReactNode;
    };

export type CardProps = CardBaseProps & CardLoadingProps;

export function Card(
  { ref, isActive = false,
    isColumnHeader = false,
    isDisabled = false,
    isLoading = false,
    loadingLabel,
    isDragOver = false,
    droppable = false,
    className,
    onClick,
    onKeyDown,
    children,
    ...rest
  }: CardProps & { ref?: Ref<HTMLElement> },
) {
  const interactive = Boolean(onClick) && !isDisabled && !isLoading;
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    onKeyDown?.(event);
    if (interactive && event.target === event.currentTarget && !event.defaultPrevented && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      event.currentTarget.click();
    }
  };
  return (
    <article
      ref={ref}
      className={cx(
        'card',
        isColumnHeader && 'is-column-header',
        isActive && 'is-active',
        isDisabled && 'is-disabled',
        isLoading && 'is-loading',
        isDragOver && 'is-drag-over',
        className,
      )}
      data-droppable={droppable || undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-disabled={isDisabled || undefined}
      aria-busy={isLoading || undefined}
      onClick={isDisabled || isLoading ? undefined : onClick}
      onKeyDown={interactive ? handleKeyDown : onKeyDown}
      {...rest}
    >
      {children}
      {isLoading && <p className="card-loading-label">{loadingLabel}</p>}
    </article>
  );
}

export type CardHeaderProps = ComponentProps<'h3'>;

export function CardHeader({ className, ...props }: CardHeaderProps) {
  return <h3 className={cx('card-header', className)} {...props} />;
}

export type CardBodyProps = ComponentProps<'p'>;

export function CardBody({ className, ...props }: CardBodyProps) {
  return <p className={cx('card-body', className)} {...props} />;
}

export interface CardSpinnerProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children' | 'title'> {
  label: string;
}

export function CardSpinner(
  { ref, label, className, ...rest }: CardSpinnerProps & { ref?: Ref<HTMLSpanElement> },
) {
  return (
    <span
      ref={ref}
      role="status"
      aria-label={label}
      className={cx('card-spinner', className)}
      {...rest}
    />
  );
}

export function LoadingState({ children, className, ...props }: Omit<HTMLAttributes<HTMLElement>, 'children'> & { children: ReactNode }) {
  return <Card {...props} className={cx('loading-state', className)} role="status" isLoading loadingLabel={children} />;
}
